import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type AgentId } from '../lib/api'
import { useAgentAvailable } from '../lib/agent-availability'
import { daysUntilLoginExpiry } from '../lib/claude-expiry'
import { ClaudeInstallCta } from './ClaudeInstallCta'
import { ClaudeLoginCta } from './ClaudeLoginCta'
import { ClaudeTokenSettings } from './ClaudeTokenSettings'
import { ClaudeUpdateCta } from './ClaudeUpdateCta'
import { CopilotLoginCta } from './CopilotLoginCta'
import { LocalSkillsSettings } from './LocalSkillsSettings'

const AGENT_ORDER: AgentId[] = ['claude', 'copilot', 'opencode']
// Literal, untranslated display names — mirrors ChatPanel.tsx's own AGENT_LABEL convention (the
// agent picker's names are proper nouns, not UI chrome).
const AGENT_LABEL: Record<AgentId, string> = { claude: 'Claude Code', copilot: 'GitHub Copilot', opencode: 'OpenCode' }

/**
 * The Settings page's "AI agent" section: one card per known CLI, reporting whether the passerelle
 * detected it (path, native/shim, and how it was found) — and, for Claude specifically, one of
 * three MUTUALLY EXCLUSIVE states: an assisted-install CTA while undetected (ClaudeInstallCta), an
 * assisted sign-in CTA once detected but not authenticated (ClaudeLoginCta — `claude auth status`
 * says `loggedIn: false`, e.g. right after a fresh install), or the ready state with the sticky
 * "Enable local skills" preference (LocalSkillsSettings — it configures a CLI that has to actually
 * be usable, not just present, to mean anything).
 *
 * Two further affordances layer ON TOP of the ready state rather than replacing it, because the
 * conditions they describe leave a perfectly usable CLI:
 *
 * - **Update available** (`versionOk === false`) — below the version floor, where an SDK session
 *   closing mid-refresh can drop the sign-in. A warning, never a gate. The update itself is ALWAYS
 *   offered on a signed-in card, next to "Niobe uses: <path> (<version>)" (AB#370): a new model can
 *   demand a newer CLI than any floor Niobe knows of.
 * - **Expires soon** (`loginExpiresAt` within three days) — the hard login expiry that refreshing
 *   never extends; only a new sign-in resets it.
 *
 * Neither ever takes the local-skills settings off the card: that is the only UI that can change
 * them, and losing it would be a real regression for a member whose CLI merely needs updating.
 *
 * GitHub Copilot gets the same assisted install while undetected (the same ClaudeInstallCta, with
 * `agent="copilot"`) — offered only when the passerelle sends a Copilot install command, since an
 * older one refuses that install. Once detected, its sign-in state is TRI-STATE: the passerelle
 * reads it in the background and leaves `authenticated` out until it knows, so only a strict
 * `false` shows "Sign-in required" and the assisted sign-in (CopilotLoginCta). A Copilot installed
 * from this page offers that sign-in straight away, as its next step, while the status is unknown.
 *
 * Detection only means anything inside Niobe Desktop (same gate as the rest of the local-AI
 * surface, see lib/agent-availability.ts) — a browser cannot reach the passerelle that runs the
 * detection, so this renders a plain explanatory note there instead of an empty/broken list.
 */
export function AiAgentSection() {
  const { t } = useLang()
  const agentAvailable = useAgentAvailable()
  const statusQuery = useQuery({
    queryKey: ['agent-status'],
    queryFn: () => api.agentStatus(),
    enabled: agentAvailable,
    staleTime: 10_000,
  })
  // Agents installed from this page in this session. The install CTA unmounts the moment the
  // refetched status detects the CLI, so a next step the member still has to take lives here.
  const [installedHere, setInstalledHere] = useState<ReadonlySet<AgentId>>(() => new Set())
  const onInstalled = (id: AgentId): void => {
    setInstalledHere((prev) => new Set(prev).add(id))
    void statusQuery.refetch()
  }

  if (!agentAvailable) return <p className="settings-note">{t('settings_ai_agent_unavailable')}</p>

  return (
    <div className="settings-agentlist">
      {AGENT_ORDER.map((id) => {
        const detail = statusQuery.data?.agents[id]
        // Claude requires a strict `true` (not just "not false") — same fail-safe stance as
        // ChatPanel's isAgentUsable. Copilot is the other way round: only a strict `false` counts,
        // because absent means its (slow) status read has not answered yet, and an unknown value
        // must never put a working Copilot behind a sign-in. OpenCode has no auth check.
        const needsLogin =
          !!detail && (id === 'claude' ? detail.authenticated !== true : id === 'copilot' && detail.authenticated === false)
        // Claude-only, and STRICTLY `false`: `undefined` means the version could not be read (an
        // older passerelle, an unparsable `--version`, another agent) and must show nothing at all.
        const outdated = !!detail && !needsLogin && detail.versionOk === false
        // Whole days until the login's hard expiry, or null when it is further off than the warning
        // window / unknown. Only meaningful on a card that IS signed in — a member who has to sign
        // in anyway does not need to be told their old login is about to expire.
        //
        // Silent while an opt-in long-lived token is what authenticates (#55): the member's
        // `/login` credential stays on disk and keeps expiring, but nothing is using it, so
        // "Your Claude sign-in expires in N days" and a Renew button beside "Signed in with a
        // long-lived token" would be two contradictory claims. The passerelle already omits the
        // field in that mode; asserting it here too means a newer web against an older passerelle
        // cannot regress it, and the warning returns the moment the token is removed or rejected.
        const longLivedToken = detail?.tokenMode === 'long-lived'
        const expiresInDays =
          detail && !needsLogin && !longLivedToken ? daysUntilLoginExpiry(detail.loginExpiresAt) : null
        const badgeClass = !detail ? '' : needsLogin ? ' warn' : outdated ? ' upd' : expiresInDays !== null ? ' warn' : ' cat'
        const badgeLabel = !detail
          ? t('settings_agent_not_detected')
          : needsLogin
            ? t('settings_agent_needs_login')
            : outdated
              ? t('settings_agent_update_available')
              : expiresInDays !== null
                ? t('settings_agent_expiring')
                : t('settings_agent_detected')
        return (
          <div className="settings-card" key={id}>
            <div className="settings-card-head">
              <b>{AGENT_LABEL[id]}</b>
              <span className={`badge${badgeClass}`}>{badgeLabel}</span>
            </div>
            {detail && (
              <p className="settings-agent-detail mono">
                {detail.path} — {detail.kind},{' '}
                {t(
                  detail.via === 'override'
                    ? 'settings_agent_via_override'
                    : detail.via === 'path'
                      ? 'settings_agent_via_path'
                      : detail.via === 'claude-desktop'
                        ? 'settings_agent_via_claude_desktop'
                        : 'settings_agent_via_known_dir',
                )}
                {detail.account ? ` — ${detail.account}` : ''}
              </p>
            )}
            {id === 'claude' && detail && !needsLogin && (
              <>
                {/* Both of these are ADDITIVE to the ready state, never a replacement for it: an
                    outdated CLI and an expiring login are both usable, so the member keeps the
                    "Enable local skills" switch and its per-skill toggles either way —
                    LocalSkillsSettings is the only UI that can change them. */}
                {/* Which Claude Code Niobe actually runs — the newest one on the machine (AB#370) —
                    and the update, always on offer: a model can demand a newer CLI at any time.
                    Below the floor, the CTA keeps its own "older builds…" warning. */}
                <p className="settings-agent-detail mono">
                  {t('settings_agent_niobe_uses').replace('{path}', () => detail.path)}
                  {detail.version ? ` (${detail.version})` : ''}
                </p>
                <ClaudeUpdateCta
                  currentVersion={detail.version}
                  minVersion={detail.minVersion ?? ''}
                  {...(outdated ? {} : { note: t('claude_update_latest_note') })}
                  onUpdated={() => void statusQuery.refetch()}
                />
                {expiresInDays !== null && (
                  <div className="claude-expiry">
                    <p className="claude-expiry-note">
                      {expiresInDays === 0
                        ? t('claude_expiry_today')
                        : expiresInDays === 1
                          ? t('claude_expiry_one_day')
                          : t('claude_expiry_days').replace('{n}', () => String(expiresInDays))}
                    </p>
                    {/* The ordinary assisted sign-in — it works perfectly well while a valid login
                        is still there, and a new sign-in is the ONLY thing that resets the hard
                        expiry (refreshing never moves it). */}
                    <ClaudeLoginCta label={t('claude_expiry_renew')} onSignedIn={() => void statusQuery.refetch()} />
                  </div>
                )}
                <LocalSkillsSettings />
              </>
            )}
            {id === 'claude' && detail && needsLogin && <ClaudeLoginCta onSignedIn={() => void statusQuery.refetch()} />}
            {/* The opt-in long-lived token, BELOW whichever branch above rendered — never instead
                of one. It is absent unless the desktop shell exposes its bridge (the flag is off by
                default), and it renders its own state from that bridge rather than from the status
                above, so there is one source on screen. */}
            {id === 'claude' && detail && <ClaudeTokenSettings />}
            {id === 'claude' && !detail && !statusQuery.isLoading && (
              <ClaudeInstallCta
                // The passerelle owns the install command — it is the member's own OS that decides
                // it, and it must be exactly what the assisted install will run. Undefined against
                // an older passerelle, and the CTA then shows no command rather than a guess.
                command={statusQuery.data?.installCommands?.claude}
                onInstalled={() => onInstalled('claude')}
              />
            )}
            {id === 'copilot' && !detail && statusQuery.data?.installCommands?.copilot && (
              <ClaudeInstallCta
                agent="copilot"
                command={statusQuery.data.installCommands.copilot}
                onInstalled={() => onInstalled('copilot')}
              />
            )}
            {/* One element for both reasons to sign in, so a sign-in already running is not
                remounted when the status read lands mid-flow (unknown → signed out). */}
            {id === 'copilot' && detail && (needsLogin || (installedHere.has('copilot') && detail.authenticated !== true)) && (
              <div className="claude-install">
                {!needsLogin && <p>{t('copilot_install_next_login')}</p>}
                <CopilotLoginCta onSignedIn={() => void statusQuery.refetch()} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
